'use client';

import { useToolUsageReport } from '@/components/tools/use-tool-usage-report';
import { useMemo, useState } from 'react';
import {
  getTextToolkitMeta,
  runTextTool,
  type TextToolkitVariant,
} from '@/lib/browser-text-toolkit';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserTextToolkit({ variant }: { variant: TextToolkitVariant }) {
  const meta = useMemo(() => getTextToolkitMeta(variant), [variant]);
  const [input, setInput] = useState('');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  // Shuffle is random, so recomputing it on every render would reshuffle the list
  // whenever anything else changes state, including pressing Copy.
  const result = useMemo(() => runTextTool(variant, input), [variant, input]);
  // `variant` is the slug (morse-code-translator, number-list-items and friends), and the
  // input starts empty, so a non-empty input means the visitor actually used the tool.
  useToolUsageReport(variant, input.trim().length > 0);

  async function handleCopy(value: string) {
    try {
      await copyText(value);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{meta.badge}</span>
        <h2 className="tool-live-title">{meta.title}</h2>
        <p className="tool-live-subtitle">
          Type or paste your text and results update live in your browser. Nothing is uploaded and
          no server is used.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>{meta.inputLabel}</span>
            <textarea
              onChange={(event) => {
                setCopyMessage(null);
                setInput(event.target.value);
              }}
              placeholder={meta.sample}
              rows={8}
              value={input}
            />
          </label>
          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              onClick={() => {
                setCopyMessage(null);
                setInput('');
              }}
              type="button"
            >
              Clear
            </button>
            {meta.kind === 'output' && result.output ? (
              <button
                className="button button-secondary"
                onClick={() => handleCopy(result.output ?? '')}
                type="button"
              >
                Copy result
              </button>
            ) : null}
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          {meta.kind === 'stats' ? (
            <div className="tool-html-meta-grid">
              {(result.stats ?? []).map((stat) => (
                <div className="tool-live-field" key={stat.label}>
                  <span>{stat.label}</span>
                  <input readOnly type="text" value={stat.value} />
                </div>
              ))}
            </div>
          ) : (
            <label className="tool-live-field tool-live-field-full">
              <span>{meta.outputLabel}</span>
              <textarea readOnly rows={8} value={result.output ?? ''} />
            </label>
          )}
          <p className="tool-live-panel-note">
            Everything runs locally in your browser. Your text is never uploaded.
          </p>
        </section>
      </div>
    </div>
  );
}
